import axios from 'axios'
import { computed, reactive, ref } from 'vue'
import useLocalStorage from '@/composables/useLocalStorage'
import type { RecoveryMethodKey } from './methods'

export interface MethodSelection {
  methods: RecoveryMethodKey[]
}

export interface RecoveryException {
  id: string
  storeIds: number[]
  selection: MethodSelection
}

interface ApiOverride {
  store_id: number
  methods: RecoveryMethodKey[]
}

interface ApiConfig {
  online_order_url: string | null
  default_methods: RecoveryMethodKey[]
  overrides: ApiOverride[]
}

const createDefaultSelection = (): MethodSelection => ({
  methods: ['qr_code']
})

const cloneSelection = (s: MethodSelection): MethodSelection => ({
  methods: [...s.methods]
})

const defaultSelection = reactive<MethodSelection>(createDefaultSelection())
const onlineUrl = ref('')
const exceptions = ref<RecoveryException[]>([])
const isLoading = ref(false)
const isSaving = ref(false)
const loadFailed = ref(false)
const loadedMerchantId = ref<number | null>(null)
const lastSavedSnapshot = ref('')

let idSeq = 0
const nextId = () => `exc-${++idSeq}`

const groupOverrides = (overrides: ApiOverride[]): RecoveryException[] => {
  const bySignature = new Map<string, RecoveryException>()

  for (const { store_id, methods } of overrides) {
    const signature = [...methods].sort().join('|')
    const existing = bySignature.get(signature)

    if (existing) existing.storeIds.push(store_id)
    else bySignature.set(signature, { id: nextId(), storeIds: [store_id], selection: { methods: [...methods] } })
  }

  return [...bySignature.values()]
}

const flattenExceptions = (): ApiOverride[] =>
  exceptions.value.flatMap((exc) =>
    exc.storeIds.map((storeId) => ({ store_id: storeId, methods: [...exc.selection.methods] }))
  )

const buildPayload = (): ApiConfig => ({
  online_order_url: onlineUrl.value.trim() || null,
  default_methods: defaultSelection.methods,
  overrides: flattenExceptions()
})

export function useRecoveryConfig() {
  const { t } = useI18n()
  const { user } = useLocalStorage()
  const notification = useNotification()

  const endpoint = () => `/merchants/${user.value.merchant.id}/reward-retrieval-config`

  const isOnlineUsedAnywhere = computed(
    () =>
      defaultSelection.methods.includes('online') ||
      exceptions.value.some((e) => e.selection.methods.includes('online'))
  )

  const isSelectionValid = (s: MethodSelection) => s.methods.length >= 1

  const storeIdsTakenByOtherExceptions = (currentExceptionId: string | null) =>
    exceptions.value.filter((e) => e.id !== currentExceptionId).flatMap((e) => e.storeIds)

  const upsertException = (payload: RecoveryException) => {
    const idx = exceptions.value.findIndex((e) => e.id === payload.id)
    if (idx === -1) exceptions.value.push(payload)
    else exceptions.value[idx] = payload
  }

  const removeException = (id: string) => {
    exceptions.value = exceptions.value.filter((e) => e.id !== id)
  }

  const newException = (): RecoveryException => ({
    id: nextId(),
    storeIds: [],
    selection: { methods: [] }
  })

  const load = async () => {
    isLoading.value = true
    loadFailed.value = false
    try {
      const { data } = await axios.get<{ data: ApiConfig }>(endpoint())
      const config = data.data

      defaultSelection.methods = [...config.default_methods]
      onlineUrl.value = config.online_order_url ?? ''
      exceptions.value = groupOverrides(config.overrides)
      loadedMerchantId.value = user.value.merchant.id
      lastSavedSnapshot.value = JSON.stringify(buildPayload())
    } catch {
      loadFailed.value = true
      notification.create('error', {
        text: t('pages.loyaltyPage.tabs.recovery.notifications.loadError'),
        timeout: 4000
      })
    } finally {
      isLoading.value = false
    }
  }

  const ensureLoaded = async () => {
    if (loadedMerchantId.value !== user.value.merchant.id) await load()
  }

  const isValid = computed(
    () =>
      isSelectionValid(defaultSelection) &&
      exceptions.value.every((exc) => isSelectionValid(exc.selection)) &&
      !(isOnlineUsedAnywhere.value && onlineUrl.value.trim().length === 0)
  )

  const save = async () => {
    const payload = buildPayload()
    isSaving.value = true
    try {
      await axios.put(endpoint(), payload)
      lastSavedSnapshot.value = JSON.stringify(payload)
      notification.create('success', { text: t('pages.loyaltyPage.tabs.recovery.notifications.saved'), timeout: 4000 })
    } catch (error: any) {
      const responseData = error.response?.data
      const validationError = responseData?.errors ? Object.values(responseData.errors)[0]?.[0] : null
      const message =
        validationError || responseData?.message || t('pages.loyaltyPage.tabs.recovery.notifications.saveError')
      notification.create('error', { text: message, timeout: 4000 })
    } finally {
      isSaving.value = false
    }
  }

  const autoSave = async () => {
    if (isLoading.value || isSaving.value || !isValid.value) return
    if (JSON.stringify(buildPayload()) === lastSavedSnapshot.value) return
    await save()
  }

  return {
    defaultSelection,
    onlineUrl,
    exceptions,
    isLoading,
    isSaving,
    loadFailed,
    isValid,
    isOnlineUsedAnywhere,
    isSelectionValid,
    storeIdsTakenByOtherExceptions,
    upsertException,
    removeException,
    newException,
    cloneSelection,
    createDefaultSelection,
    load,
    ensureLoaded,
    save,
    autoSave
  }
}
